import React, { useEffect, useId, useState } from "react";
import {
  Layers,
  Puzzle,
  RefreshCw,
  CarFront,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { isValidWaitlistEmail } from "./waitlistValidation";
import { submitWaitlist, warmupWaitlistBackend } from "./submitWaitlist";

/** Fichiers dans `public/rcmatchdb/` (copiés depuis `frontend/src/assets/` via `npm run sync-rcmatchdb-assets`). */
function rcmatchdbPublicAsset(filename) {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/rcmatchdb/${filename}`;
}

const logoSrc = rcmatchdbPublicAsset("rcmatchdb_logo.svg");
const bannerSrc = rcmatchdbPublicAsset("rcmatchdb_banner.png");
const bgHomeSrc = rcmatchdbPublicAsset("rcmatchdb_background_homepage.png");

const BRANDS = [
  "Arrma",
  "Traxxas",
  "Losi",
  "Tekno RC",
  "Team Corally",
  "HPI Racing",
  "Rlaarlo",
  "FMS",
];

const CANONICAL = "https://www.rcmatchdb.com/";
const OG_TITLE = "RCMatchDB — RC Vehicle & Parts Compatibility Database";
const META_DESC =
  "Find compatible RC parts, OEM references, exploded views and upgrade alternatives for RC cars and trucks.";

function injectMetaTags(options = {}) {
  const { ogImageUrl } = options;
  const added = [];

  const upsertMeta = (attrs) => {
    const sel = attrs.property
      ? `meta[property="${attrs.property}"]`
      : `meta[name="${attrs.name}"]`;
    let el = document.head.querySelector(sel);
    if (!el) {
      el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    } else {
      const prev = el.getAttribute("content");
      el.setAttribute("content", attrs.content);
      added.push({ el, prevContent: prev });
    }
  };

  const prevTitle = document.title;
  document.title = OG_TITLE;

  upsertMeta({ name: "description", content: META_DESC });
  upsertMeta({ property: "og:title", content: OG_TITLE });
  upsertMeta({ property: "og:description", content: META_DESC });
  upsertMeta({ property: "og:type", content: "website" });
  upsertMeta({ property: "og:url", content: CANONICAL });
  upsertMeta({ name: "twitter:card", content: "summary_large_image" });

  if (ogImageUrl) {
    upsertMeta({ property: "og:image", content: ogImageUrl });
    upsertMeta({ name: "twitter:image", content: ogImageUrl });
  }

  let canonical = document.querySelector('link[rel="canonical"]');
  let canonicalCreated = false;
  let prevHref = null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
    canonicalCreated = true;
  } else {
    prevHref = canonical.getAttribute("href");
  }
  canonical.setAttribute("href", CANONICAL);

  return () => {
    document.title = prevTitle;
    added.forEach((item) => {
      if (item.el && item.prevContent !== undefined) {
        if (item.prevContent === null) item.el.removeAttribute("content");
        else item.el.setAttribute("content", item.prevContent);
      } else if (item instanceof HTMLElement) {
        item.remove();
      }
    });
    if (canonicalCreated) canonical.remove();
    else if (prevHref !== null) canonical.setAttribute("href", prevHref);
    else canonical.removeAttribute("href");
  };
}

/** Optional Plausible (domain only) or GA4 measurement ID — injected only if env set at build time. */
function OptionalAnalytics() {
  useEffect(() => {
    const plausibleDomain = (process.env.REACT_APP_PLAUSIBLE_DOMAIN || "").trim();
    const gaId = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
    const scripts = [];

    if (plausibleDomain) {
      const s = document.createElement("script");
      s.defer = true;
      s.dataset.domain = plausibleDomain;
      s.src = "https://plausible.io/js/script.js";
      document.head.appendChild(s);
      scripts.push(s);
    }

    if (gaId) {
      const gtagJs = document.createElement("script");
      gtagJs.async = true;
      gtagJs.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
      document.head.appendChild(gtagJs);
      scripts.push(gtagJs);

      const inline = document.createElement("script");
      inline.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId.replace(/'/g, "\\'")}');
      `;
      document.head.appendChild(inline);
      scripts.push(inline);
    }

    return () => {
      scripts.forEach((n) => {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    };
  }, []);
  return null;
}

export default function LandingPage() {
  const emailFieldId = useId();
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  /** null | "demo" (aucun POST réel) | "saved" (réponse API) */
  const [waitlistOutcome, setWaitlistOutcome] = useState(null);

  useEffect(() => {
    const ogImageUrl =
      typeof window !== "undefined"
        ? new URL(bannerSrc, window.location.origin).href
        : "";
    return injectMetaTags(ogImageUrl ? { ogImageUrl } : {});
  }, []);

  // Wake Render free-tier instance early (cold start can take ~30–60s otherwise).
  useEffect(() => {
    void warmupWaitlistBackend({ force: true });
  }, []);

  const showEmailError = touched && email.length > 0 && !isValidWaitlistEmail(email);

  async function onSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (!isValidWaitlistEmail(email)) {
      setStatus("error");
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    setWaitlistOutcome(null);
    try {
      const result = await submitWaitlist(email);
      setWaitlistOutcome(result.mock ? "demo" : "saved");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message || "Something went wrong. Please try again.");
    }
  }

  return (
    <div className="landing-root relative min-h-screen text-slate-100 antialiased selection:bg-cyan-500/30">
      <OptionalAnalytics />

      <div
        className="pointer-events-none fixed inset-0 z-0 bg-[#070b12] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(
            165deg,
            rgba(7, 11, 18, 0.94) 0%,
            rgba(7, 11, 18, 0.78) 40%,
            rgba(7, 11, 18, 0.92) 100%
          ), url(${bgHomeSrc})`,
        }}
        aria-hidden
      />
      <div
        className="landing-grid-bg pointer-events-none fixed inset-0 z-[1] opacity-[0.06]"
        aria-hidden
      />

      <header className="relative z-10 border-b border-white/10 bg-[#070b12]/50 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <a
            href="#top"
            className="flex shrink-0 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#070b12] rounded-md"
          >
            <img
              src={logoSrc}
              alt="RCMatchDB"
              className="h-9 w-auto sm:h-11"
              width={180}
              height={44}
              decoding="async"
            />
          </a>
          <a
            href="#waitlist"
            className="rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-1.5 text-sm font-medium text-cyan-100 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            Join the waitlist
          </a>
        </div>
      </header>

      <main id="top" className="relative z-10 mx-auto max-w-5xl px-4 pb-20 pt-12 sm:px-6 sm:pt-16">
        <section className="text-center sm:text-left">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-orange-200">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-400" aria-hidden />
            Coming soon
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-white drop-shadow-sm sm:text-5xl md:text-6xl">
            RCMatchDB
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 sm:text-xl">
            The universal RC compatibility database
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
            Find compatible parts, OEM references, exploded views and upgrade alternatives for RC
            vehicles.
          </p>

          <div className="mt-10 overflow-hidden rounded-2xl border border-white/15 bg-black/20 shadow-2xl shadow-black/50 ring-1 ring-white/5 backdrop-blur-[2px]">
            <img
              src={bannerSrc}
              alt="RCMatchDB — RC vehicle and parts compatibility"
              className="block w-full object-cover object-center"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </div>
        </section>

        <section
          id="waitlist"
          className="landing-card mt-12 rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-xl shadow-cyan-950/40 backdrop-blur-md sm:p-8"
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Join the waitlist</h2>
              <p className="mt-1 text-sm text-slate-400">Get notified when RCMatchDB launches.</p>
            </div>
          </div>

          {status === "success" ? (
            <div className="mt-6 space-y-2" role="status" aria-live="polite">
              <p className="flex items-start gap-2 text-base text-cyan-200">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-400" aria-hidden />
                Thanks — we&apos;ll notify you when RCMatchDB launches.
              </p>
              {waitlistOutcome === "demo" ? (
                <p className="pl-7 text-sm text-amber-200/90">
                  Preview mode: no server URL was configured — your email was{" "}
                  <strong className="font-semibold">not saved</strong>.
                </p>
              ) : null}
            </div>
          ) : (
            <form className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-start" onSubmit={onSubmit}>
              <div className="flex-1">
                <label htmlFor={emailFieldId} className="sr-only">
                  Email address
                </label>
                <input
                  id={emailFieldId}
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  onBlur={() => setTouched(true)}
                  aria-invalid={showEmailError}
                  aria-describedby={showEmailError ? `${emailFieldId}-err` : undefined}
                  className="w-full rounded-lg border border-white/15 bg-[#0c1220] px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-cyan-500/60 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                />
                {showEmailError ? (
                  <p id={`${emailFieldId}-err`} className="mt-2 text-sm text-orange-300" role="alert">
                    Please enter a valid email address.
                  </p>
                ) : null}
                {status === "error" && errorMessage ? (
                  <p className="mt-2 flex items-start gap-2 text-sm text-orange-300" role="alert">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    {errorMessage}
                  </p>
                ) : null}
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 font-semibold text-white shadow-lg shadow-cyan-900/40 transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "Sending…" : "Notify me"}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </form>
          )}
        </section>

        <section className="mt-16 grid gap-4 sm:grid-cols-2">
          {[
            {
              icon: CarFront,
              title: "Vehicle database",
              text: "Browse RC vehicles and technical specifications.",
            },
            {
              icon: Puzzle,
              title: "Parts compatibility",
              text: "Discover which parts fit your vehicle.",
            },
            {
              icon: RefreshCw,
              title: "OEM alternatives",
              text: "Find equivalent replacement and upgrade components.",
            },
            {
              icon: Layers,
              title: "Exploded views",
              text: "Navigate parts lists and diagrams by vehicle.",
            },
          ].map(({ icon: Icon, title, text }) => (
            <article
              key={title}
              className="landing-card rounded-xl border border-white/10 bg-white/[0.035] p-5 backdrop-blur-sm transition hover:border-cyan-500/25"
            >
              <Icon className="h-8 w-8 text-cyan-400" strokeWidth={1.5} aria-hidden />
              <h3 className="mt-3 text-lg font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{text}</p>
            </article>
          ))}
        </section>

        <section className="mt-16 rounded-2xl border border-orange-500/20 bg-orange-500/[0.06] p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-orange-100">Early supported brands</h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {BRANDS.map((name) => (
              <li
                key={name}
                className="rounded-md border border-white/10 bg-black/30 px-3 py-1.5 text-sm text-slate-200"
              >
                {name}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-white">Where we are today</h2>
          <p className="mt-3 text-slate-300">
            RCMatchDB is currently in early development. We are building the first public RC vehicle
            and parts compatibility index.
          </p>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-[#050810]/85 py-10 text-center text-sm text-slate-500 backdrop-blur-sm">
        <p className="text-slate-400">Built for RC builders, bashers and racers.</p>
        <p className="mt-4">
          © {new Date().getFullYear()} RCMatchDB ·{" "}
          <a href="mailto:contact@rcmatchdb.com" className="text-cyan-400 hover:underline">
            contact@rcmatchdb.com
          </a>
        </p>
      </footer>

      <style>{`
        .landing-root {
          --accent: #22d3ee;
          --accent2: #fb923c;
        }
        .landing-grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent);
        }
        .landing-card {
          box-shadow: inset 0 1px 0 0 rgba(255,255,255,0.06);
        }
        .font-display {
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif;
          letter-spacing: -0.03em;
        }
      `}</style>
    </div>
  );
}
